import React, { useRef, useState } from "react";
import Grid from "@mui/material/Grid2";
import { Button, MenuItem, Stack, Typography } from "@mui/material";
import MaterialTable from "material-table";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { withStyles } from "@mui/styles";
import { Styles } from "../../../../config/design/styles";
import { defaultVariables } from "../../../../config";
import { TableCount, TablePage } from "../../../../config/Permissions";
import { getDataTableLocalization } from "../../../../config/Translate";
import { tableIcons } from "../../../../config/design/TableIcons";
import { showTableDate } from "../../../../config/values";
import AppAnimate from "../../../components/AppAnimate";
import Title from "../../../components/elements/Title";
import RayaField from "../../../components/elements/fields/RayaField";

function IyzipayApprovalsPage({ classes }) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const tableRef = useRef();
    const [statusFilter, setStatusFilter] = useState("all");
    const [selectedRows, setSelectedRows] = useState([]);
    const [busy, setBusy] = useState(false);

    const loginToken = localStorage.getItem("ACT");
    const config = {
        headers: {
            Accept: "application/json",
            Authorization: `Bearer ${loginToken}`,
        },
    };

    const reload = () => {
        setSelectedRows([]);
        tableRef.current && tableRef.current.onQueryChange();
    };

    const postAction = async (url, body) => {
        setBusy(true);
        try {
            await axios.post(`${defaultVariables.url}${url}`, body, config);
            enqueueSnackbar(t("iyzipayApprovalActionSuccess"), { variant: "success" });
            reload();
        } catch (e) {
            enqueueSnackbar(e?.response?.data?.message || t("iyzipayApprovalActionFailed"), {
                variant: "error",
            });
        } finally {
            setBusy(false);
        }
    };

    const handleBulk = async (action) => {
        if (!selectedRows.length) {
            enqueueSnackbar(t("iyzipayApprovalSelectFirst"), { variant: "warning" });
            return;
        }
        setBusy(true);
        try {
            const res = await axios.post(
                `${defaultVariables.url}/iyzipay-approvals/bulk`,
                { ids: selectedRows.map((r) => r.id), action },
                config
            );
            const ok = res.data?.ok || 0;
            const fail = res.data?.fail || 0;
            enqueueSnackbar(t("iyzipayApprovalBulkResult", { ok, fail }), {
                variant: fail && !ok ? "error" : "success",
            });
            reload();
        } catch (e) {
            enqueueSnackbar(e?.response?.data?.message || t("iyzipayApprovalActionFailed"), {
                variant: "error",
            });
        } finally {
            setBusy(false);
        }
    };

    const handleSync = async () => {
        setBusy(true);
        try {
            const res = await axios.post(
                `${defaultVariables.url}/iyzipay-approvals/sync`,
                { days: 30 },
                config
            );
            const upserted = res.data?.reporting?.upserted || 0;
            const backfill = res.data?.backfill || 0;
            enqueueSnackbar(t("iyzipayApprovalSyncResult", { upserted, backfill }), {
                variant: "success",
            });
            reload();
        } catch (e) {
            enqueueSnackbar(e?.response?.data?.message || t("iyzipayApprovalActionFailed"), {
                variant: "error",
            });
        } finally {
            setBusy(false);
        }
    };

    const statusLabel = (status) => t(`iyzipayApprovalStatus_${status}`) || status;

    const columns = [
        {
            title: t("registerDate"),
            field: "payment_at",
            editable: "never",
            render: (rowData) => (
                <Typography className={classes.dateTable}>
                    {showTableDate(rowData.payment_at || rowData.created_at)}
                </Typography>
            ),
        },
        {
            title: t("orderNumber"),
            field: "order_number",
            editable: "never",
            render: (rowData) => rowData.order?.number || "-",
        },
        {
            title: t("amount"),
            field: "paid_price",
            editable: "never",
            render: (rowData) =>
                rowData.paid_price != null ? `${rowData.paid_price} ${rowData.currency || "TRY"}` : "-",
        },
        {
            title: t("iyzipayCommission"),
            field: "commission_total",
            editable: "never",
            render: (rowData) => {
                const total = rowData.commission_total;
                if (total == null || Number(total) === 0) {
                    if (rowData.iyzi_commission_fee == null && rowData.iyzi_commission_rate_amount == null) {
                        return "-";
                    }
                }
                return `${Number(total || 0).toFixed(2)} ${rowData.currency || "TRY"}`;
            },
        },
        {
            title: t("iyzipayNetPayout"),
            field: "net_payout",
            editable: "never",
            render: (rowData) =>
                rowData.net_payout != null
                    ? `${Number(rowData.net_payout).toFixed(2)} ${rowData.currency || "TRY"}`
                    : "-",
        },
        {
            title: "paymentId",
            field: "payment_id",
            editable: "never",
        },
        {
            title: t("status"),
            field: "approval_status",
            editable: "never",
            width: 140,
            render: (rowData) => (
                <Typography
                    component="span"
                    className={`table-badge ${
                        rowData.approval_status === "approved"
                            ? "active"
                            : rowData.approval_status === "waiting" || rowData.approval_status === "failed"
                              ? "pending"
                              : "inactive"
                    }`}
                >
                    {statusLabel(rowData.approval_status)}
                </Typography>
            ),
        },
        {
            title: t("iyzipayRefundStatus"),
            field: "refund_status",
            editable: "never",
            width: 140,
            render: (rowData) => (
                <Typography
                    component="span"
                    className={`table-badge ${
                        rowData.refund_status === "full"
                            ? "inactive"
                            : rowData.refund_status === "partial"
                              ? "pending"
                              : "neutral"
                    }`}
                >
                    {t(`iyzipayRefundStatus_${rowData.refund_status || "none"}`)}
                    {rowData.refunded_amount
                        ? ` (${rowData.refunded_amount})`
                        : ""}
                </Typography>
            ),
        },
        {
            title: t("activity"),
            field: "actions",
            editable: "never",
            sorting: false,
            render: (rowData) => (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {rowData.approval_status === "cancelled" && (
                        <Typography component="span" className="table-badge inactive">
                            {t("iyzipayApprovalStatus_cancelled")}
                        </Typography>
                    )}
                    {(rowData.approval_status === "waiting" ||
                        rowData.approval_status === "failed" ||
                        rowData.approval_status === "disapproved") &&
                        rowData.refund_status !== "full" && (
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            disabled={busy}
                            onClick={() => postAction("/iyzipay-approvals/approve", { id: rowData.id })}
                        >
                            {t("iyzipayApprovalApprove")}
                        </Button>
                    )}
                    {rowData.approval_status === "approved" && rowData.refund_status !== "full" && (
                        <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            disabled={busy}
                            onClick={() => postAction("/iyzipay-approvals/disapprove", { id: rowData.id })}
                        >
                            {t("iyzipayApprovalDisapprove")}
                        </Button>
                    )}
                    {(rowData.approval_status === "waiting" ||
                        rowData.approval_status === "failed" ||
                        rowData.approval_status === "disapproved") &&
                        rowData.refund_status !== "full" && (
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={busy}
                            onClick={() => postAction("/iyzipay-approvals/cancel", { id: rowData.id })}
                        >
                            {t("iyzipayApprovalCancel")}
                        </Button>
                    )}
                    {!!rowData.can_refund && rowData.approval_status !== "cancelled" && (
                        <Button
                            size="small"
                            variant="contained"
                            color="error"
                            disabled={busy}
                            onClick={() => {
                                if (window.confirm(t("iyzipayRefundConfirm"))) {
                                    postAction("/iyzipay-approvals/refund", { id: rowData.id });
                                }
                            }}
                        >
                            {t("iyzipayApprovalRefund")}
                        </Button>
                    )}
                </Stack>
            ),
        },
    ];

    return (
        <AppAnimate animation="transition.slideUpIn" delay={200} title={t("iyzipayApprovalsTitle")}>
            <Grid container spacing={2}>
                <Grid item size={12}>
                    <Title align="left" color="text" className="panel-title">
                        {t("iyzipayApprovalsTitle")}
                    </Title>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        {t("iyzipayApprovalsDesc")}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        {t("iyzipayApprovalsStatusHint")}
                    </Typography>
                </Grid>

                <Grid item size={{ xs: 12, md: 4 }}>
                    <RayaField
                        select
                        fullWidth
                        variant="outlined"
                        label={t("status")}
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setTimeout(reload, 0);
                        }}
                    >
                        <MenuItem value="all">{t("all")}</MenuItem>
                        <MenuItem value="waiting">{t("iyzipayApprovalStatus_waiting")}</MenuItem>
                        <MenuItem value="approved">{t("iyzipayApprovalStatus_approved")}</MenuItem>
                        <MenuItem value="disapproved">{t("iyzipayApprovalStatus_disapproved")}</MenuItem>
                        <MenuItem value="cancelled">{t("iyzipayApprovalStatus_cancelled")}</MenuItem>
                        <MenuItem value="failed">{t("iyzipayApprovalStatus_failed")}</MenuItem>
                    </RayaField>
                </Grid>

                <Grid item size={12}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button variant="outlined" disabled={busy} onClick={handleSync}>
                            {t("iyzipayApprovalSync")}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={busy || !selectedRows.length}
                            onClick={() => handleBulk("approve")}
                        >
                            {t("iyzipayApprovalBulkApprove")} ({selectedRows.length})
                        </Button>
                        <Button
                            variant="outlined"
                            color="warning"
                            disabled={busy || !selectedRows.length}
                            onClick={() => handleBulk("disapprove")}
                        >
                            {t("iyzipayApprovalBulkDisapprove")}
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            disabled={busy || !selectedRows.length}
                            onClick={() => handleBulk("cancel")}
                        >
                            {t("iyzipayApprovalBulkCancel")}
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            disabled={busy || !selectedRows.length}
                            onClick={() => {
                                if (window.confirm(t("iyzipayRefundBulkConfirm"))) {
                                    handleBulk("refund");
                                }
                            }}
                        >
                            {t("iyzipayApprovalBulkRefund")}
                        </Button>
                    </Stack>
                </Grid>

                <Grid item size={12} className="main-table">
                    <MaterialTable
                        tableRef={tableRef}
                        icons={tableIcons}
                        title=" "
                        localization={getDataTableLocalization()}
                        columns={columns}
                        data={(query) =>
                            new Promise((resolve) => {
                                let url = `${defaultVariables.url}/iyzipay-approvals/quickView?page=${
                                    query.page + 1
                                }&per_page=${query.pageSize}&search=${encodeURIComponent(query.search || "")}`;
                                if (statusFilter !== "all") url += `&status=${statusFilter}`;

                                axios
                                    .get(url, config)
                                    .then((response) => {
                                        resolve({
                                            data: response.data?.data || [],
                                            page: (response.data?.current_page || 1) - 1,
                                            totalCount: response.data?.total || 0,
                                        });
                                    })
                                    .catch(() => resolve({ data: [], page: 0, totalCount: 0 }));
                            })
                        }
                        onSelectionChange={(rows) => setSelectedRows(rows || [])}
                        options={{
                            pageSize: TablePage,
                            pageSizeOptions: TableCount,
                            sorting: false,
                            draggable: false,
                            selection: true,
                            showTextRowsSelected: true,
                            tableLayout: "auto",
                        }}
                    />
                </Grid>
            </Grid>
        </AppAnimate>
    );
}

export default withStyles(Styles)(IyzipayApprovalsPage);
